/**
 * ⟦A-4 décision 2⟧ L'écran **« ce qui change »** de la création d'avatar.
 *
 * « Choix homme/femme. JAMAIS de stat genrée type "jauge de charisme" — des événements et
 * pondérations différenciés, **bonus ET malus des deux côtés**, présentés par un écran honnête.
 * Ton : bienveillant, rigolo, vrai, jamais moqueur. »
 *
 * ⚠️ **Les textes sont ceux de `docs/EVENEMENTS-GENRES.md` section A, et ils sont EN
 * RELECTURE.** L'arbitrage **A-4 H** en fait une condition d'acceptation : rien de genré
 * n'entre dans `content/` sans la passe commune avec Mathieu. Cet écran n'est pas du contenu —
 * c'est de l'interface — et il est ici pour que Mathieu relise la matière **en contexte**,
 * ce qui est plus honnête qu'un tableau markdown. Les corriger, c'est corriger ce fichier ET
 * le document, dans le même commit.
 *
 * La règle d'écriture qui tient tout (2e passe de A-4) : **le stéréotype vient toujours de
 * l'extérieur** — commentaires, sponsors, journalistes — jamais du personnage du joueur ni de
 * la voix du jeu. Une femme n'y est jamais réduite à la sexualisation, un homme jamais réduit
 * au bourrin.
 *
 * ## ⟦A-38⟧ La fiche ne promet plus que ce qui existe
 *
 * Les **textes** (titre, « ce que ça fait », « ce que ça apporte », chute) sont ceux que Mathieu
 * a relus et validés ⟦A-27⟧ : ils ne bougent pas d'une virgule.
 *
 * La **fiche de run**, elle, annonçait trois choses que le moteur ne fait pas — et ⟦A-27⟧ les
 * avait pourtant listées noir sur blanc dans son tableau « ce que le contenu n'a PAS pu dire » :
 *
 * 1. *« actif si niche gaming/tech »* — la **niche de chaîne** est NON IMPLÉMENTÉE en v1
 *    (SPEC-MOTEUR §7.4) et `EventConditions` n'a aucun champ de thème. L'événement existe, il
 *    est gardé sur `subs_min`, rien d'autre ;
 * 2. *« le prochain gros coup dur est amorti de moitié »* — les flags `reseau_solidaire` /
 *    `mentor` sont bien posés, mais **aucun des 19 types d'effets ne réduit les dégâts d'un
 *    événement à venir**. Le filet reste à écrire ;
 * 3. *« Image +2 »* — `channel_image` est une valeur **dérivée** (§7.1) que rien n'écrit ;
 *    l'effet réellement présent dans `content/events.json` est `gauge_delta reputation +2`.
 *
 * Ce qui **existe bel et bien** et reste donc affiché : les poids genrés (`poids_genre` dans
 * `content/events.json` depuis ⟦A-27⟧, lus par `poidsModule` depuis ⟦A-34⟧), les deux traits, et
 * les événements nommés — vérifiés un par un dans la donnée avant d'écrire cette fiche.
 *
 * ⚠️ Les seuils et les effets chiffrés ci-dessous sont **lus dans `content/events.json`** au
 * rendu, jamais recopiés : c'est ce qui empêche l'écran de re-mentir au prochain réglage.
 */

import {
  MULT_TOXIQUES_F,
  MULT_FIDELITE_F,
  MULT_REPUTATION_H,
  MULT_SM_HORS_SOMMEIL_H,
  type GaugeId,
} from "@maxime/shared";
import { contenu } from "../../game/content";
import type { GenreAvatar } from "../../game/gameClient";
import { systemeOuvert } from "../../game/menage";

/** 1.2 → « 1,2 » : la fiche parle français, comme tout l'écran. */
const fr = (n: number) => n.toLocaleString("fr-FR");

const NOM_JAUGE: Record<GaugeId, string> = {
  energie: "Énergie",
  sante_mentale: "Santé mentale",
  reputation: "Réputation",
  fidelite: "Fidélité",
  argent: "Clicks",
  momentum: "Momentum",
  inspiration: "Inspiration",
  // ⟦SPEC-FIL §8.3 · M3.22⟧ La 8ᵉ jauge est entrée dans `GaugeId` : un événement de contenu
  // peut désormais chauffer ou refroidir la communauté. Le `Record` exhaustif a fait tomber
  // l'oubli à la compilation — c'est exactement ce pour quoi il est écrit comme ça.
  hype: "Hype",
};

// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-38⟧ Ce que la fiche dit vient de `content/events.json`, jamais d'une recopie
// ─────────────────────────────────────────────────────────────────────────────

/** « dès 500 abonnés » — le seuil réellement porté par `conditions.subs_min`. */
function seuilAbonnes(eventId: string): string {
  const min = contenu().parEvenement.get(eventId)?.conditions?.subs_min;
  return min === undefined ? "sans seuil d'abonnés" : `dès ${fr(min)} abonnés`;
}

/** Le multiplicateur de poids réellement écrit dans `poids_genre` (⟦A-27⟧, lu par ⟦A-34⟧). */
function poidsGenre(eventId: string, genre: GenreAvatar): string {
  const p = contenu().parEvenement.get(eventId)?.poids_genre?.[genre];
  return p === undefined ? "×1 (normal)" : p === 1 ? "×1 (normal)" : `×${fr(p)}`;
}

/**
 * ⟦A-53⟧ La branche citée par la fiche, qu'elle soit **un choix** (le joueur tranche) ou
 * **l'issue unique d'une notification** (elle s'applique seule et part au récapitulatif).
 * Les quatre lignes de genre ci-dessous citent les deux familles depuis A-53 ; sans cette
 * résolution, la fiche annoncerait « — » et ⟦A-34⟧ mentirait à l'écran de création.
 */
function brancheCitee(eventId: string, choiceId: string) {
  const e = contenu().parEvenement.get(eventId);
  return e?.choix?.find((c) => c.id === choiceId) ?? e?.issue;
}

/** « Fidélité +3, Réputation +2 » — les `gauge_delta` de la branche, tels que le moteur les appliquera. */
function effetsDuChoix(eventId: string, choiceId: string): string {
  const choix = brancheCitee(eventId, choiceId);
  if (!choix) return "—";
  // ⟦C60 bis · A-177⟧ La réputation a quitté l'écran (arbitrage n° 2, 03/10/2026) : son effet existe toujours dans
  // le contenu et dans le moteur, la fiche ne le montre plus. Rallumée, la fiche redit « Réputation +2 ».
  const reputationVisible = systemeOuvert("reputation");
  const bouts = choix.effets.flatMap((e) =>
    e.type === "gauge_delta" && e.value !== 0 && (reputationVisible || e.gauge !== "reputation")
      ? [
          e.gauge === "inspiration"
            ? `${e.value > 0 ? "+" : "−"}${Math.abs(e.value)} idée${Math.abs(e.value) > 1 ? "s" : ""}`
            : `${NOM_JAUGE[e.gauge]} ${e.value > 0 ? "+" : "−"}${Math.abs(e.value)}`,
        ]
      : [],
  );
  return bouts.length > 0 ? bouts.join(", ") : "rien de chiffré";
}

/** « choix « Assumer en réponse épinglée » → Fidélité +3, Réputation +2 ». */
function choixLisible(eventId: string, choiceId: string): string {
  const label = brancheCitee(eventId, choiceId)?.label;
  // ⟦A-53⟧ Une notification n'a plus de choix : l'annoncer comme tel serait faux.
  const prefixe = contenu().parEvenement.get(eventId)?.choix === undefined ? "" : "choix ";
  return `${prefixe}« ${label ?? choiceId} » → ${effetsDuChoix(eventId, choiceId)}`;
}

interface LigneFiche {
  /**
   * ⟦C23 §1⟧ Le libellé COURT affiché dans le tableau (« Fidélité », « Commentaires troll »…).
   * Le nom complet de la règle (`variable`) ne disparaît pas : il passe en info-bulle.
   */
  libelle: string;
  /** Le nom complet de la variable, inchangé, affiché en info-bulle au survol/tap (`title`). */
  variable: string;
  valeur: string;
}

interface Panneau {
  titre: string;
  ceQueCaFait: string;
  ceQueCaApporte: string;
  chute: string;
  /**
   * La fiche technique de la run : les VRAIES variables qui changent (poids d'événements,
   * événements exclusifs, flags). ⟦A-38⟧ Chaque ligne est **vérifiable dans la donnée** — un
   * `poids_genre`, un `subs_min`, un `gauge_delta`. Rien de ce que le moteur ne fait pas.
   */
  fiche: LigneFiche[];
}

/**
 * `docs/EVENEMENTS-GENRES.md` §A — les **textes** recopiés à la lettre, pas réécrits.
 *
 * ⟦A-38⟧ C'est une fonction et plus une constante : la fiche interroge `content/events.json` au
 * rendu. Une constante de module ferait tourner le parse Zod du contenu à l'import, et surtout
 * elle rendrait à nouveau possible ce qu'on vient de réparer — une valeur recopiée qui survit à
 * la donnée qu'elle décrit.
 */
export function ceQuiChange(genre: GenreAvatar): Panneau {
  return PANNEAUX[genre]();
}

const PANNEAUX: Record<GenreAvatar, () => Panneau> = {
  f: () => ({
    titre: "On ne va pas se mentir. Être une femme sur MeTube, ça change des choses.",
    ceQueCaFait:
      "On commentera parfois tes cheveux avant ton travail. Dans certaines niches, on te demandera de prouver deux fois ce qu'un autre affirme une fois. Et tu penseras à ta sécurité un peu plus tôt que les autres.",
    ceQueCaApporte:
      "Les communautés qui se construisent autour des créatrices comptent parmi les plus fidèles et les plus solidaires de la plateforme. Des marques cherchent activement des voix comme la tienne là où il n'y en a pas assez. Et chaque palier que tu franchis en ouvre un peu plus grand la porte pour les suivantes.",
    chute: "Rien de tout ça ne te définit. C'est le terrain, pas la joueuse.",
    fiche: [
      {
        libelle: "Fidélité",
        variable: "TRAIT « La communauté-maison »",
        valeur: `gains de Fidélité ×${fr(MULT_FIDELITE_F)}`,
      },
      {
        libelle: "Commentaires troll",
        variable: "TRAIT « Le Fil est plus rude »",
        // ⟦M3.21⟧ Le trait a changé de place, donc la fiche change de phrase : il ne renchérit
        // plus le COÛT de la lecture (`coutCommentaires`, retiré), il pèse sur le TIRAGE. Un
        // commentaire toxique coûte la même chose à tout le monde ; il en arrive plus souvent.
        valeur: `commentaires troll et toxiques ×${fr(MULT_TOXIQUES_F)} plus fréquents dans le Fil`,
      },
      {
        libelle: "Commentaires apparence",
        variable: "Poids « Tout sauf la vidéo » (commentaires apparence)",
        valeur: poidsGenre("tout_sauf_la_video_f", "f"),
      },
      {
        libelle: "Vulnérabilité",
        variable: "Poids « La vulnérabilité » (faiblesse montrée)",
        valeur: poidsGenre("vulnerabilite_f", "f"),
      },
      {
        libelle: "Sponsor près de la plaque",
        variable: "Poids « Sponsor à côté de la plaque »",
        valeur: poidsGenre("sponsor_a_cote_de_la_plaque_f", "f"),
      },
      {
        libelle: "Niche fermée",
        variable: "« Pas pour toi, cette niche »",
        valeur: `${seuilAbonnes("pas_pour_toi_cette_niche_f")} — la niche de chaîne n'existe pas en v1, l'événement ne la regarde pas`,
      },
      {
        libelle: "Question famille",
        variable: "« La question famille »",
        valeur: `version interview, ${seuilAbonnes("question_famille_f")}`,
      },
      {
        libelle: "Main tendue",
        variable: "« La main tendue » (réseau d'entraide)",
        valeur: `${seuilAbonnes("main_tendue_f")} · ${choixLisible("main_tendue_f", "accepter")}`,
      },
      {
        libelle: "Jour sans filtre",
        variable: "« Le jour sans filtre »",
        valeur: choixLisible("jour_sans_filtre_f", "assumer"),
      },
    ],
  }),
  h: () => ({
    titre: "On ne va pas se mentir. Être un homme sur MeTube, ça change des choses.",
    ceQueCaFait:
      "On t'insultera aussi — mais personne ne trouvera ça grave, pas même toi : il paraît que « c'est le jeu ». Le jour où tu montreras une faiblesse, la moitié criera au calcul et l'autre moitié te dira de te bouger. Quand tu iras vraiment mal, peu de gens le remarqueront — on ne s'inquiète pas pour ceux qui sont censés tout encaisser. Et tu seras un mec de plus sur une plateforme qui en déborde : personne ne t'attend.",
    ceQueCaApporte:
      "On te supposera compétent avant que tu aies rien prouvé — c'est injuste, et c'est confortable. On parlera de tes vidéos plus que de ton corps. Et tes coups de gueule passeront pour du caractère, là où d'autres se feraient démonter pour moins que ça.",
    chute: "Rien de tout ça ne te définit. C'est le terrain, pas le joueur.",
    fiche: [
      // ⟦C60 bis⟧ Ce trait ne change plus rien que le joueur puisse voir : sa ligne n'est écrite que si la réputation l'est.
      ...(systemeOuvert("reputation")
        ? [
            {
              libelle: "Réputation",
              variable: "TRAIT « Le préjugé favorable »",
              valeur: `gains de Réputation ×${fr(MULT_REPUTATION_H)}`,
            },
          ]
        : []),
      {
        libelle: "Santé mentale",
        variable: "TRAIT « Personne ne s'inquiète »",
        valeur: `remontées de Santé mentale hors sommeil ×${fr(MULT_SM_HORS_SOMMEIL_H)} (le sommeil, lui, est intact)`,
      },
      {
        libelle: "Commentaires apparence",
        variable: "Poids « Tout sauf la vidéo » (commentaires apparence)",
        valeur: poidsGenre("tout_sauf_la_video_h", "h"),
      },
      {
        libelle: "Vulnérabilité",
        variable: "Poids « La vulnérabilité » (faiblesse montrée)",
        valeur: poidsGenre("vulnerabilite_h", "h"),
      },
      {
        libelle: "Sponsor près de la plaque",
        variable: "Poids « Sponsor à côté de la plaque »",
        valeur: poidsGenre("sponsor_a_cote_de_la_plaque_h", "h"),
      },
      {
        libelle: "Niche fermée",
        variable: "« Pas pour toi, cette niche »",
        valeur: `${seuilAbonnes("pas_pour_toi_cette_niche_h")} — la niche de chaîne n'existe pas en v1, l'événement ne la regarde pas`,
      },
      {
        libelle: "Question famille",
        variable: "« La question famille »",
        valeur: `version repas de famille, ${seuilAbonnes("question_famille_h")}`,
      },
      {
        libelle: "Main tendue",
        variable: "« La main tendue » (mentor)",
        valeur: `${seuilAbonnes("main_tendue_h")} · ${choixLisible("main_tendue_h", "accepter")}`,
      },
      {
        libelle: "Jour sans filtre",
        variable: "« Le jour sans filtre »",
        valeur: `rien ne se passe → ${effetsDuChoix("jour_sans_filtre_h", "rien_ne_se_passe")}`,
      },
    ],
  }),
};

export function CeQuiChange({ genre }: { genre: GenreAvatar }) {
  const p = ceQuiChange(genre);
  return (
    <section className="ce-qui-change" aria-live="polite">
      <p className="cqc-titre">{p.titre}</p>
      <p className="cqc-bloc">
        <strong>Ce que ça fait :</strong> {p.ceQueCaFait}
      </p>
      <p className="cqc-bloc">
        <strong>Ce que ça apporte :</strong> {p.ceQueCaApporte}
      </p>
      <p className="cqc-chute">{p.chute}</p>
      <p className="cqc-mecanique">
        <strong>Ta fiche de run</strong> — jauges, formules et mini-jeux : identiques pour tout le
        monde. Ce qui change vraiment, en chiffres :
      </p>
      {/* ⟦C23 §1⟧ Retour de Matt : « les stats, ce n'est pas très agréable à lire. Un vrai
          tableau avec des lignes, en mode 8 bits. » Même contenu qu'avant (`ceQuiChange`
          inchangé), rendu en tableau pixel : en-tête violet, lignes alternées crème/crème
          tramé, libellés courts (le nom complet de la règle passe en info-bulle `title`,
          même mécanisme que les tooltips pédagogiques de `Jauge.tsx`). */}
      <table className="cqc-fiche">
        <thead>
          <tr>
            <th scope="col">Ce qui change</th>
            <th scope="col">Effet</th>
          </tr>
        </thead>
        <tbody>
          {p.fiche.map((ligne) => (
            <tr key={ligne.variable}>
              <td className="cqc-fiche-libelle" title={ligne.variable}>
                {ligne.libelle}
              </td>
              <td className="cqc-fiche-valeur">{ligne.valeur}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
